/**
 * Provider-section graft — preserve user evidence when a refreshed provider
 * slot replaces an existing SED section.
 *
 * WHY THIS EXISTS (bug report 2026-09-04, site `site-1783519433189-x9azl1`,
 * power-availability `power-availability-1788532480440-m1sq6p`).
 * `topUpSiteSed`'s graft loop replaced a whole section slot with the freshly
 * compiled provider slot:
 *
 *     if (newSlot && newSlot.status !== 'unavailable') merged[sec] = newSlot;
 *
 * `compileSiteEvidence` builds that slot from provider data ONLY. For `grid`
 * it emits exactly {substationName, substationDistKm, voltage, capacity,
 * complexity, substationsWithin50km, transmissionLines, nearestTransmissionKm,
 * nearestTransmissionVoltage, estimatedInterconnectionCost, estimatedQueueTime,
 * isoRegion} — with `substationName` defaulting to the literal `'Unknown'`.
 * So a scoped `hifld-grid` refresh (Refresh Evidence panel, or "rerun with
 * refresh" on the grid section) did three destructive things at once:
 *
 *   1. REVERTED every provider-owned field the user had amended — the reported
 *      substation name went from the user's "Centerville Substation" back to
 *      the HIFLD 115 kV row, while `sed.amendments[]` (a SED-ROOT array, not
 *      touched by the graft) kept asserting the amendment was live. The report
 *      then read the reverted value and the operator read the audit trail, and
 *      the two disagreed silently.
 *   2. DELETED the authored-only fields the provider compile cannot produce —
 *      `capacityDetail` (the utility letter's stated MW), `interconnectionClass`,
 *      `utilityName`. Losing `capacityDetail` drops the tier-1
 *      candidate for `grid.headroomIndicatedMw` (see `factDescriptors.ts`), so
 *      the canonical store falls through to the tier-2 site-context CLAIM —
 *      which is `statusWhenWinner: 'labeled-unverifiable'`. That is why the
 *      report started attributing available headroom to an unverified "user"
 *      claim instead of the utility letter.
 *
 *   3. CUT DOCUMENT LINKS. `linkedDocumentIds` / `linkedDocumentRefs` are set
 *      by the user and the DM2 matcher, never by the compile, so every graft
 *      severed them — including, absurdly, links `topUpSiteSed`'s own
 *      `includeDocIds` block had stamped onto `existing` moments earlier in
 *      the same call.
 *
 * The rules below are deliberately different, because the field classes have
 * different owners:
 *
 *   - PROVIDER-OWNED fields (the compile emits them): the refresh is entitled
 *     to move them. Amendments are NOT re-applied here — that is a whole-SED
 *     post-pass (`reapplyInBandAmendments`), because stampers and the doc-
 *     override layer run AFTER this graft and would undo anything it restored.
 *   - AUTHORED-ONLY fields (the compile never emits them): the refresh has
 *     nothing to say about them, so dropping them is pure data loss. Carried
 *     forward verbatim.
 *   - SLOT-LEVEL DOCUMENT LINKS: a doc↔section relationship, independent of
 *     any particular provider value. Carried forward (unioned). Distinct from
 *     the doc↔value derivation record, which is NOT — see the two
 *     "deliberately absent" notes below.
 *
 * The allow-list is deliberate rather than structural ("keep any key the new
 * data lacks"). A provider-owned field that simply did not come back this run
 * must NOT be resurrected from the previous run — silently re-asserting a
 * stale `capacity: 'High'` is exactly the direction that makes a GO verdict
 * easier, which is the dangerous direction.
 *
 * Pure — no I/O, no client deps, safe for the Cloud Run bootstrap bundle.
 */
import type { EvidenceSection } from '../../types/siteEvidence';
import { sedValuesEqual } from './sedValueEquality';

/**
 * Per-section fields that are USER-AUTHORED and have no provider producer, so
 * a provider graft must carry them forward rather than drop them.
 *
 * Derived by diffing each section's evidence interface against what
 * `compileSiteEvidence` actually writes. Keep it that way: a field belongs
 * here only when the compile CANNOT emit it. If a provider ever starts
 * producing one of these, remove it here in the same commit — otherwise the
 * graft would pin the first authored value forever.
 */
export const AUTHORED_ONLY_SECTION_FIELDS: Readonly<Record<string, readonly string[]>> = {
  grid: [
    // X-SED-7 — the utility letter's stated available capacity + service
    // class. Written by `authoredSchemaToSed`; HIFLD has no such concept.
    'capacityDetail',
    'interconnectionClass',
    // Serving-utility name: sourced from the utility-service-letter doc
    // override (`sedDocResolver`) / site research, never from the HIFLD
    // substation compile. Not coupled to any provider-owned number — the
    // serving utility does not change because HIFLD re-measured a distance.
    'utilityName',
  ],
};

/*
 * DELIBERATELY ABSENT from `grid` — do NOT add these back:
 *
 *   substationDistKmProvenance, nearestTransmissionKmProvenance,
 *   transmissionOperator
 *
 * They are KSD2 / KSI stamps written by `compileOnSiteInfrastructure`
 * ATOMICALLY WITH the value they describe (`substationDistKm` /
 * `nearestTransmissionKm` / the matched corridor). That compile runs in
 * `bootstrapSiteSed`, NOT in `topUpSiteSed` — so carrying a stamp across a
 * graft while the refreshed HIFLD number replaces the value it annotates
 * would assert that a provider distance is user-KMZ ground truth. Losing the
 * stamp with its value is a gap; keeping the stamp without its value is a
 * provenance lie, and only one of those is recoverable. Re-binding both
 * belongs to the KMZ cross-bind, not to this graft.
 */

/**
 * SLOT-level document LINKS, carried across every grafted section.
 *
 * A link is a relationship between a document and a SECTION ("this utility
 * letter is evidence for grid"), established by the user or the DM2 matcher.
 * Re-running HIFLD does not unlink a document from a topic, and
 * `compileSiteEvidence`'s `section()` factory never emits these — so the graft
 * was silently cutting doc lineage every time it refreshed a section. Worse,
 * `topUpSiteSed`'s `includeDocIds` block stamps `linkedDocumentIds` onto
 * `existing` slots IMMEDIATELY BEFORE the graft loop runs, so a caller that
 * linked a doc and refreshed a provider in one call lost the link it had just
 * asked for. The Document Library's doc→section reverse index
 * (`src/lib/docSedReverseIndex.ts`) is built from these, so the loss surfaces
 * as a document that claims to feed no sections.
 *
 * The two are carried TOGETHER on purpose: `linkedDocumentIds` is documented
 * as the back-compat projection of `linkedDocumentRefs.map(r => r.docId)`
 * (DM4), so preserving one without the other desyncs the ranked view from the
 * legacy view.
 */
const PRESERVED_SLOT_LINK_FIELDS = ['linkedDocumentIds', 'linkedDocumentRefs'] as const;

/*
 * HUMAN FIELD REVIEWS — carried as a surgical nested copy, NOT by preserving
 * the whole `provenanceDetail`.
 *
 * `provenanceDetail.fieldReviews` records the operator's accepted / corrected /
 * REJECTED rulings on machine-extracted fields (`applyFieldReview` in
 * `lib/coverage/extractedFieldReview.ts`). `applyRequirementDocAutoFill` reads
 * it as `priorReviews` and refuses to re-stamp a field a human has ruled on.
 *
 * `topUpSiteSed` calls `applyDocAwareConfidence` — which re-runs that stamper
 * — a few hundred lines AFTER the graft loop. So clearing `fieldReviews` here
 * meant the same top-up that grafted the section then happily re-stamped the
 * machine value a human had explicitly rejected, and the review panel showed
 * the field as never reviewed. The guard was defeated inside the very call
 * that was supposed to honour it.
 *
 * The SIBLINGS of `fieldReviews` are deliberately NOT carried: `providerValue`,
 * `docValue`, `mergeReason`, `autoFillProvider(s)` and `autoFillFields` all
 * describe the merge that produced the `data` this graft just replaced.
 * Carrying them would attribute the OLD merge to the NEW value — the same
 * provenance lie as the KMZ stamps. A review decision is about a FIELD and a
 * human; a merge record is about a VALUE.
 */
const PRESERVED_PROVENANCE_REVIEW_KEY = 'fieldReviews';

/*
 * DELIBERATELY NOT carried across a graft:
 *
 *   supportingDocIds, docContributionScore, adjustedConfidence
 *
 * These are not links — they are a DERIVATION/BOOST record about the specific
 * `data` the graft just replaced. `supportingDocIds` lists the docs a section
 * was derived from or boosted BY; `adjustedConfidence` overrides `confidence`
 * downstream (the confidence-aware hedging helper) with a number computed
 * against the OLD value. Carrying them
 * onto fresh, unboosted provider data would assert that a document supports a
 * figure it never saw, and would inflate the displayed confidence of a value
 * nothing has corroborated. Same class as the KMZ provenance stamps above:
 * losing a record with its value is a gap; keeping it is a lie.
 */

export interface GraftProviderSectionResult<T = unknown> {
  /** The slot to write onto the SED. */
  slot: EvidenceSection<T>;
  /** Authored-only `data` keys carried forward from the existing slot. */
  preservedFields: string[];
  /** Slot-level document-link fields carried forward from the existing slot. */
  preservedLinkFields: string[];
}

/**
 * Union two document-link lists, existing entries first, deduped by document
 * id. Returns `undefined` when neither side has anything.
 *
 * The provider compile emits no links today, so in practice this carries the
 * existing list — but a union means a future producer's links are added rather
 * than silently dropped by the preservation step meant to protect them.
 */
function unionLinks(
  existing: unknown,
  provider: unknown,
): unknown[] | undefined {
  const a = Array.isArray(existing) ? existing : [];
  const b = Array.isArray(provider) ? provider : [];
  if (a.length === 0 && b.length === 0) return undefined;
  const out: unknown[] = [];
  const seen = new Set<string>();
  for (const entry of [...a, ...b]) {
    // Handles both shapes: `linkedDocumentIds` (bare id strings) and
    // `linkedDocumentRefs` (objects keyed by `docId`).
    const id = typeof entry === 'string'
      ? entry
      : (entry && typeof entry === 'object' && typeof (entry as { docId?: unknown }).docId === 'string')
        ? (entry as { docId: string }).docId
        : undefined;
    // An entry with no readable identity cannot be deduped; keep it rather
    // than drop it — this step exists to STOP losing links.
    if (id === undefined) { out.push(entry); continue; }
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(entry);
  }
  return out;
}

/**
 * Graft a refreshed provider slot onto an existing SED section without
 * destroying user evidence.
 *
 * Returns the provider slot unchanged when there is genuinely nothing to
 * preserve — so a first-fill graft stays byte-identical to the pre-fix
 * behaviour.
 */
export function graftProviderSection<T = unknown>(args: {
  sectionId: string;
  existingSlot: EvidenceSection<T> | undefined;
  providerSlot: EvidenceSection<T>;
}): GraftProviderSectionResult<T> {
  const { sectionId, existingSlot, providerSlot } = args;

  const preservedFields: string[] = [];
  const preservedLinkFields: string[] = [];

  // ── 0. Slot-level document links ──
  //
  // Runs BEFORE (and independently of) the data merge: `topUpSiteSed`'s
  // `includeDocIds` block stamps links onto ANY slot regardless of status, so
  // an `unavailable` slot with `data: null` — exactly the slot a graft is
  // about to fill — can be carrying links that must survive.
  const slotLinkPatch: Record<string, unknown> = {};
  for (const key of PRESERVED_SLOT_LINK_FIELDS) {
    const existingLinks = (existingSlot as Record<string, unknown> | undefined)?.[key];
    if (!Array.isArray(existingLinks) || existingLinks.length === 0) continue;
    const providerLinks = (providerSlot as unknown as Record<string, unknown>)[key];
    const merged = unionLinks(existingLinks, providerLinks);
    if (!merged) continue;
    // Nothing gained — leave the provider slot's own value untouched.
    if (sedValuesEqual(merged, providerLinks)) continue;
    slotLinkPatch[key] = merged;
    preservedLinkFields.push(key);
  }

  // ── 0b. Human field-review decisions ──
  const existingProvenance = (existingSlot as { provenanceDetail?: Record<string, unknown> } | undefined)
    ?.provenanceDetail;
  const existingReviews = existingProvenance?.[PRESERVED_PROVENANCE_REVIEW_KEY];
  if (
    existingReviews
    && typeof existingReviews === 'object'
    && Object.keys(existingReviews as Record<string, unknown>).length > 0
  ) {
    const providerProvenance = (providerSlot as unknown as { provenanceDetail?: Record<string, unknown> })
      .provenanceDetail;
    if (providerProvenance?.[PRESERVED_PROVENANCE_REVIEW_KEY] === undefined) {
      slotLinkPatch.provenanceDetail = {
        // Keep whatever the refreshed slot says about its OWN merge; graft only
        // the human rulings on top. `mergeReason` is required on the type, so a
        // provider slot without provenanceDetail gets an explicit one naming
        // why the object exists at all.
        ...(providerProvenance ?? { mergeReason: 'provider-only' }),
        [PRESERVED_PROVENANCE_REVIEW_KEY]: JSON.parse(JSON.stringify(existingReviews)) as unknown,
      };
      preservedLinkFields.push('provenanceDetail.fieldReviews');
    }
  }

  const existingData = existingSlot?.data;
  const providerData = providerSlot?.data;
  const bothAreObjects =
    !!existingData && typeof existingData === 'object' && !Array.isArray(existingData)
    && !!providerData && typeof providerData === 'object' && !Array.isArray(providerData);

  if (!bothAreObjects) {
    return {
      slot: preservedLinkFields.length > 0
        ? { ...providerSlot, ...slotLinkPatch }
        : providerSlot,
      preservedFields,
      preservedLinkFields,
    };
  }

  const prior = existingData as unknown as Record<string, unknown>;
  const next: Record<string, unknown> = { ...(providerData as unknown as Record<string, unknown>) };

  // ── 1. Authored-only fields the provider compile cannot produce ──
  for (const key of AUTHORED_ONLY_SECTION_FIELDS[sectionId] ?? []) {
    if (next[key] !== undefined) continue;       // provider does own it after all
    const authored = prior[key];
    if (authored === undefined) continue;        // nothing authored to carry
    // Deep-copy object/array values so the grafted slot never aliases the
    // slot it replaced — the caller keeps a reference to both.
    next[key] = authored !== null && typeof authored === 'object'
      ? (JSON.parse(JSON.stringify(authored)) as unknown)
      : authored;
    preservedFields.push(key);
  }

  return {
    slot: { ...providerSlot, ...slotLinkPatch, data: next as unknown as T },
    preservedFields,
    preservedLinkFields,
  };
}
